import React from 'react';

function About({ onBack }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#1a2942] to-[#0d47a1] text-white">
      {/* Navigation */}
      <nav className="flex justify-between items-center px-16 py-6 relative z-10">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="TwoRun.AI Logo" className="w-12 h-12 object-contain" />
          <span className="text-2xl font-bold">TwoRun.AI</span>
        </div>
        
        <div className="flex gap-4">
          <button 
            onClick={onBack}
            className="px-6 py-2 border border-white/30 rounded-lg hover:border-white hover:bg-white/10 transition-all"
          >
            ← Back to Home
          </button>
        </div>
      </nav>

      {/* About Content */}
      <div className="max-w-5xl mx-auto px-8 py-16">
        <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
          About TwoRun.AI
        </h1>
        
        <div className="space-y-8 text-lg text-white/80 leading-relaxed">
          <section>
            <h2 className="text-3xl font-bold text-white mb-4">What is TwoRun.AI?</h2>
            <p>
              TwoRun.AI is a national artificial intelligence platform developed by Baku Business Group and OAWO Inc. 
              Our mission is to expand the application of artificial intelligence in the real sector and contribute to 
              Azerbaijan's digital development.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-4">Our Vision</h2>
            <p className="italic text-2xl text-blue-300 mb-4">
              "Where Human and Artificial Intelligence Run Together"
            </p>
            <p>
              We believe in creating decision-making systems based on the collaboration between human and artificial 
              intelligence. Our platform aims to transform Azerbaijan into a regional leader in AI innovation.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-4">Key Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="text-xl font-bold text-blue-300 mb-3">🔒 Secure & Local</h3>
                <p>All data processed on encrypted local servers, ensuring complete data sovereignty and security.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="text-xl font-bold text-blue-300 mb-3">🎯 Industry-Specific</h3>
                <p>Customized AI models trained on your industry data, speaking your business language.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="text-xl font-bold text-blue-300 mb-3">📊 Advanced Analytics</h3>
                <p>Predictive analytics, behavior modeling, and real-time data processing capabilities.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="text-xl font-bold text-blue-300 mb-3">🌱 Sustainable</h3>
                <p>Data centers powered entirely by green energy, ensuring environmental responsibility.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-4">Application Areas</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                'Education', 'Healthcare', 'Transportation', 
                'Finance', 'Energy', 'Agriculture',
                'Manufacturing', 'Government', 'Logistics'
              ].map((area, index) => (
                <div key={index} className="bg-blue-500/20 px-4 py-3 rounded-lg text-center font-semibold">
                  {area}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-4">Technology Stack</h2>
            <p>
              TwoRun.AI is built on GPT-based localized models, utilizing a hybrid cloud system with local servers 
              and Azure/AWS infrastructure. We employ Python, TensorFlow, PyTorch, and JavaScript, adhering to 
              ISO 27001 and SOC2 security standards.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-4">Our Partners</h2>
            <div className="flex items-center gap-8">
              <div>
                <p className="font-bold text-blue-300 text-xl mb-2">Baku Business Group MMC</p>
                <p>Strategic management and technological coordination</p>
              </div>
              <div>
                <p className="font-bold text-blue-300 text-xl mb-2">OAWO Inc.</p>
                <p>Model training, technical integration, and AI infrastructure support</p>
              </div>
            </div>
          </section>

          <section className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-4">Core Values</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-blue-400 text-2xl">✓</span>
                <span><strong>Innovation:</strong> Pioneering AI solutions for real-world challenges</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-400 text-2xl">✓</span>
                <span><strong>Reliability:</strong> Trusted technology with proven results</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-400 text-2xl">✓</span>
                <span><strong>National Interests:</strong> Supporting Azerbaijan's digital transformation</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-400 text-2xl">✓</span>
                <span><strong>Technological Independence:</strong> Building sovereign AI capabilities</span>
              </li>
            </ul>
          </section>

          <section className="text-center py-8">
            <h2 className="text-3xl font-bold text-white mb-4">Join Our Journey</h2>
            <p className="mb-6">
              TwoRun.AI officially launched in 2025, and we're committed to transforming Azerbaijan into a regional 
              AI innovation hub. Partner with us to build the future of intelligent technology.
            </p>
            <a 
              href="mailto:sales@tworun.ai"
              className="inline-block px-8 py-4 bg-blue-500 rounded-xl font-semibold hover:bg-blue-600 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-500/40 transition-all"
            >
              Contact Us
            </a>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0a1628] px-16 py-8 border-t border-white/10 mt-16">
        <div className="text-center">
          <p className="text-xs text-white/40">
            🚀 TwoRun.AI by OAWO Inc. & Baku Business Group 2025
          </p>
        </div>
      </footer>
    </div>
  );
}

export default About;
